import { useEffect, useRef, useState } from "react";
import api, { apiError, assetUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { CircleNotch, Storefront as StoreIcon, UploadSimple, ArrowSquareOut, Copy, Check, CreditCard, Warning, Receipt } from "@phosphor-icons/react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

export default function StoreSettings() {
  const { t } = useTranslation();
  const [store, setStore] = useState(null);
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(false);
  const [connect, setConnect] = useState(null);
  const [orders, setOrders] = useState([]);
  const logoRef = useRef();
  const bannerRef = useRef();
  const shareRef = useRef();

  useEffect(() => {
    api.get("/store/settings").then((r) => setStore(r.data)).catch((e) => toast.error(apiError(e)));
    api.get("/connect/status").then((r) => setConnect(r.data)).catch(() => {});
    api.get("/store/orders").then((r) => setOrders(r.data)).catch(() => {});
  }, []);

  const set = (patch) => setStore((s) => ({ ...s, ...patch }));

  const publicUrl = store?.slug ? `${window.location.origin}/tienda/${store.slug}` : "";
  const shareUrl = store?.slug ? `${window.location.origin}/api/public/share/${store.slug}` : "";

  const upload = async (e, field) => {
    const f = e.target.files?.[0];
    if (!f) return;
    try {
      const fd = new FormData();
      fd.append("file", f);
      fd.append("kind", "store");
      const { data } = await api.post("/assets/upload", fd);
      set({ [field]: data.asset_id });
      toast.success(t("storesettings.imageUploaded"));
    } catch (err) { toast.error(apiError(err)); }
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/store/settings", {
        slug: store.slug, name: store.name, tagline: store.tagline, bio: store.bio,
        enabled: store.enabled, logo_asset_id: store.logo_asset_id, banner_asset_id: store.banner_asset_id,
        theme_color: store.theme_color, accent: store.accent, contact_email: store.contact_email, whatsapp: store.whatsapp, language: store.language,
        orders_email: store.orders_email,
        seller_max_discount_percent: parseFloat(store.seller_max_discount_percent) || 0,
        seller_max_discount_amount: parseFloat(store.seller_max_discount_amount) || 0,
        tax_rate: parseFloat(store.tax_rate) || 0,
        default_shipping: parseFloat(store.default_shipping) || 0,
        seller_can_edit_tax: !!store.seller_can_edit_tax,
        seller_can_edit_shipping: store.seller_can_edit_shipping !== false,
        custom_domain: store.custom_domain || "",
        share_image_asset_id: store.share_image_asset_id || null,
      });
      setStore(data);
      toast.success(t("storesettings.storeSaved"));
    } catch (err) { toast.error(apiError(err)); }
    finally { setSaving(false); }
  };

  const copy = () => { navigator.clipboard.writeText(publicUrl); setCopied(true); setTimeout(() => setCopied(false), 1500); };

  if (!store) return <div className="p-8"><CircleNotch className="animate-spin text-gold" size={26} /></div>;

  return (
    <div className="p-8 max-w-3xl" data-testid="store-settings-page">
      <div className="flex items-center gap-3 mb-2">
        <StoreIcon size={28} weight="duotone" className="text-gold" />
        <h1 className="text-3xl font-bold tracking-tight">{t("storesettings.title")}</h1>
      </div>
      <p className="text-neutral-400 mb-8">{t("storesettings.subtitle")}</p>

      {/* Stripe connect requirement */}
      {connect && !connect.connected && (
        <div className="border border-amber-500/30 rounded-xl p-5 bg-amber-500/[0.06] mb-6 flex items-start gap-3" data-testid="store-stripe-banner">
          <Warning size={22} className="text-amber-400 shrink-0 mt-0.5" weight="fill" />
          <div className="flex-1">
            <p className="font-semibold text-amber-200">{t("storesettings.connectPayTitle")}</p>
            <p className="text-xs text-neutral-300 mt-1">{t("storesettings.connectPayDesc")}</p>
            <Link to="/dashboard/settings" className="inline-flex items-center gap-1.5 mt-3 text-sm font-medium text-amber-200 hover:text-amber-100" data-testid="store-connect-stripe-link"><CreditCard size={15} /> {t("storesettings.connectStripeLink")}</Link>
          </div>
        </div>
      )}
      {connect && connect.connected && (
        <div className="border border-emerald-500/25 rounded-xl px-5 py-3 bg-emerald-500/[0.06] mb-6 flex items-center gap-2 text-sm text-emerald-300" data-testid="store-stripe-ok">
          <Check size={16} weight="bold" /> {t("storesettings.payConnected")}
        </div>
      )}

      {/* Publish + URL */}
      <div className="border border-white/10 rounded-xl p-5 bg-[#121212] mb-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="font-semibold">{t("storesettings.published")}</p>
            <p className="text-xs text-neutral-400 mt-0.5">{t("storesettings.publishedDesc")}</p>
          </div>
          <Switch checked={!!store.enabled} onCheckedChange={(v) => set({ enabled: v })} data-testid="store-enabled-switch" />
        </div>
        {store.slug && (
          <div className="mt-4 flex items-center gap-2 rounded-lg bg-black/40 border border-white/10 px-3 py-2">
            <span className="text-sm text-neutral-300 truncate flex-1" data-testid="store-public-url">{publicUrl}</span>
            <button onClick={copy} className="text-neutral-400 hover:text-gold" title={t("storesettings.copy")} data-testid="copy-store-url">{copied ? <Check size={16} className="text-emerald-400" /> : <Copy size={16} />}</button>
            <a href={publicUrl} target="_blank" rel="noreferrer" className="text-neutral-400 hover:text-gold" title={t("storesettings.open")} data-testid="open-store-url"><ArrowSquareOut size={16} /></a>
          </div>
        )}
      </div>

      <div className="border border-white/10 rounded-xl p-6 bg-[#121212] space-y-5">
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <Label>{t("storesettings.storeName")}</Label>
            <Input value={store.name || ""} onChange={(e) => set({ name: e.target.value })} className="mt-2 bg-white/5 border-white/15" data-testid="store-name-input" />
          </div>
          <div>
            <Label>{t("storesettings.address")}</Label>
            <div className="mt-2 flex items-center rounded-md bg-white/5 border border-white/15 overflow-hidden">
              <span className="pl-3 pr-1 text-neutral-500 text-sm shrink-0">/tienda/</span>
              <input value={store.slug || ""} onChange={(e) => set({ slug: e.target.value })} className="bg-transparent py-2 pr-3 text-sm w-full outline-none" placeholder={t("storesettings.slugPlaceholder")} data-testid="store-slug-input" />
            </div>
          </div>
        </div>

        <div>
          <Label>{t("storesettings.tagline")}</Label>
          <Input value={store.tagline || ""} onChange={(e) => set({ tagline: e.target.value })} className="mt-2 bg-white/5 border-white/15" placeholder={t("storesettings.taglinePlaceholder")} data-testid="store-tagline-input" />
        </div>
        <div>
          <Label>{t("storesettings.description")}</Label>
          <Textarea value={store.bio || ""} onChange={(e) => set({ bio: e.target.value })} className="mt-2 bg-white/5 border-white/15" placeholder={t("storesettings.descPlaceholder")} data-testid="store-bio-input" />
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <Label>{t("storesettings.contactEmail")}</Label>
            <Input value={store.contact_email || ""} onChange={(e) => set({ contact_email: e.target.value })} className="mt-2 bg-white/5 border-white/15" data-testid="store-email-input" />
          </div>
          <div>
            <Label>{t("storesettings.whatsapp")}</Label>
            <Input value={store.whatsapp || ""} onChange={(e) => set({ whatsapp: e.target.value })} className="mt-2 bg-white/5 border-white/15" placeholder="+1 787 000 0000" data-testid="store-whatsapp-input" />
          </div>
        </div>

        <div className="border-t border-white/10 pt-5">
          <h3 className="font-semibold mb-1">Equipo y ventas</h3>
          <p className="text-xs text-neutral-500 mb-3">Correo para nuevas órdenes y límites de descuento que pueden ofrecer tus vendedores.</p>
          <div className="grid sm:grid-cols-3 gap-4">
            <div>
              <Label>Correo de nuevas órdenes</Label>
              <Input value={store.orders_email || ""} onChange={(e) => set({ orders_email: e.target.value })} className="mt-2 bg-white/5 border-white/15" placeholder="ordenes@mitienda.com" data-testid="store-orders-email-input" />
            </div>
            <div>
              <Label>Descuento máx. vendedor (%)</Label>
              <Input type="number" min="0" value={store.seller_max_discount_percent ?? 0} onChange={(e) => set({ seller_max_discount_percent: e.target.value })} className="mt-2 bg-white/5 border-white/15" data-testid="store-max-disc-pct-input" />
            </div>
            <div>
              <Label>Descuento máx. vendedor ($)</Label>
              <Input type="number" min="0" value={store.seller_max_discount_amount ?? 0} onChange={(e) => set({ seller_max_discount_amount: e.target.value })} className="mt-2 bg-white/5 border-white/15" data-testid="store-max-disc-amt-input" />
            </div>
          </div>
          <p className="text-[11px] text-neutral-500 mt-2">Deja en 0 para no permitir ese tipo de descuento. El vendedor entra en <span className="text-gold">/vendedor</span>.</p>
        </div>

        <div className="border-t border-white/10 pt-5">
          <h3 className="font-semibold mb-1">Impuesto y envío</h3>
          <p className="text-xs text-neutral-500 mb-3">Se muestran en el desglose del precio dentro del personalizador 3D (Subtotal + Impuesto + Envío = Total). Al carrito de Wix se envía solo el subtotal; Wix aplica impuesto y envío en su propio checkout.</p>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label>Impuesto (%)</Label>
              <Input type="number" min="0" step="0.01" value={store.tax_rate ?? 0} onChange={(e) => set({ tax_rate: e.target.value })} className="mt-2 bg-white/5 border-white/15" data-testid="store-tax-rate-input" />
            </div>
            <div>
              <Label>Envío por defecto ($)</Label>
              <Input type="number" min="0" step="0.01" value={store.default_shipping ?? 0} onChange={(e) => set({ default_shipping: e.target.value })} className="mt-2 bg-white/5 border-white/15" data-testid="store-default-shipping-input" />
            </div>
          </div>
          <label className="flex items-center gap-2 text-xs text-neutral-300 mt-3" data-testid="store-can-edit-tax"><input type="checkbox" checked={!!store.seller_can_edit_tax} onChange={(e) => set({ seller_can_edit_tax: e.target.checked })} /> El vendedor puede ajustar/descontar el impuesto al tomar pedidos</label>
          <label className="flex items-center gap-2 text-xs text-neutral-300 mt-2" data-testid="store-can-edit-shipping"><input type="checkbox" checked={store.seller_can_edit_shipping !== false} onChange={(e) => set({ seller_can_edit_shipping: e.target.checked })} /> El vendedor puede ajustar/descontar el envío al tomar pedidos</label>
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <Label>{t("storesettings.logo")}</Label>
            <div className="mt-2 flex items-center gap-3">
              <div className="w-16 h-16 rounded-lg border border-white/15 overflow-hidden bg-black/40 flex items-center justify-center shrink-0">
                {store.logo_asset_id ? <img src={assetUrl(`/api/assets/${store.logo_asset_id}`)} alt="" className="w-full h-full object-cover" /> : <StoreIcon size={22} className="text-neutral-600" />}
              </div>
              <Button variant="outline" onClick={() => logoRef.current?.click()} className="border-white/15" data-testid="store-logo-upload"><UploadSimple size={16} className="mr-1" /> {t("storesettings.uploadLogo")}</Button>
              <input ref={logoRef} type="file" accept="image/*" hidden onChange={(e) => upload(e, "logo_asset_id")} />
            </div>
          </div>
          <div>
            <Label>{t("storesettings.banner")}</Label>
            <div className="mt-2 flex items-center gap-3">
              <div className="w-24 h-16 rounded-lg border border-white/15 overflow-hidden bg-black/40 flex items-center justify-center shrink-0">
                {store.banner_asset_id ? <img src={assetUrl(`/api/assets/${store.banner_asset_id}`)} alt="" className="w-full h-full object-cover" /> : <UploadSimple size={18} className="text-neutral-600" />}
              </div>
              <Button variant="outline" onClick={() => bannerRef.current?.click()} className="border-white/15" data-testid="store-banner-upload"><UploadSimple size={16} className="mr-1" /> {t("storesettings.uploadBanner")}</Button>
              <input ref={bannerRef} type="file" accept="image/*" hidden onChange={(e) => upload(e, "banner_asset_id")} />
            </div>
          </div>
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <Label>{t("storesettings.bgColor")}</Label>
            <div className="mt-2 flex items-center gap-2">
              <input type="color" value={store.theme_color || "#0B0B0F"} onChange={(e) => set({ theme_color: e.target.value })} className="w-10 h-10 rounded border border-white/15 bg-transparent" data-testid="store-theme-color" />
              <Input value={store.theme_color || ""} onChange={(e) => set({ theme_color: e.target.value })} className="bg-white/5 border-white/15 font-mono text-sm" />
            </div>
          </div>
          <div>
            <Label>{t("storesettings.accentColor")}</Label>
            <div className="mt-2 flex items-center gap-2">
              <input type="color" value={store.accent || "#C6A15B"} onChange={(e) => set({ accent: e.target.value })} className="w-10 h-10 rounded border border-white/15 bg-transparent" data-testid="store-accent-color" />
              <Input value={store.accent || ""} onChange={(e) => set({ accent: e.target.value })} className="bg-white/5 border-white/15 font-mono text-sm" />
            </div>
          </div>
        </div>

        <p className="text-xs text-neutral-500">{t("storesettings.publishedNote")}</p>

        <div>
          <Label>{t("storesettings.defaultLang")}</Label>
          <div className="mt-2 flex gap-2" data-testid="store-language-select">
            {[["en", "English"], ["es", "Español"]].map(([v, lbl]) => (
              <button key={v} type="button" onClick={() => set({ language: v })}
                className={`rounded-full px-4 py-2 text-sm border ${(store.language || "en") === v ? "bg-gold text-obsidian border-gold font-semibold" : "border-white/15 text-neutral-300 hover:bg-white/5"}`}
                data-testid={`store-lang-${v}`}>{lbl}</button>
            ))}
          </div>
          <p className="text-xs text-neutral-500 mt-1">{t("storesettings.defaultLangNote")}</p>
        </div>

        <Button onClick={save} disabled={saving} className="rounded-full bg-gold text-obsidian hover:bg-gold/90 font-semibold" data-testid="save-store-btn">
          {saving ? <CircleNotch className="animate-spin" /> : t("storesettings.saveStore")}
        </Button>
      </div>

      {/* Custom domain + share image */}
      <div className="border border-white/10 rounded-xl p-6 bg-[#121212] mt-6 space-y-6" data-testid="store-domain-share">
        <div>
          <h3 className="font-semibold mb-1">Dominio propio</h3>
          <p className="text-xs text-neutral-500 mb-3">Conecta tu propio dominio (ej. <span className="text-gold">mitienda.com</span>) para que tu tienda abra ahí en vez de /tienda/{store.slug || "tu-tienda"}.</p>
          <Label>Tu dominio</Label>
          <Input value={store.custom_domain || ""} onChange={(e) => set({ custom_domain: e.target.value })} className="mt-2 bg-white/5 border-white/15" placeholder="mitienda.com" data-testid="store-custom-domain-input" />
          <div className="mt-3 rounded-lg border border-white/10 bg-black/30 p-3 text-xs text-neutral-300 space-y-1" data-testid="domain-dns-instructions">
            <p className="font-semibold text-neutral-200">Pasos para activarlo (al publicar):</p>
            <p>1. En tu proveedor de dominio (GoDaddy, Namecheap, etc.) crea un registro <b>CNAME</b>:</p>
            <p className="pl-3 font-mono">host: <b>www</b> → valor: <b>{(typeof window !== "undefined" ? window.location.host : "tu-app")}</b></p>
            <p>2. Para el dominio raíz (sin www) usa un registro <b>A</b>/<b>ALIAS</b> según indique tu hosting al desplegar.</p>
            <p>3. Guarda aquí tu dominio y vuelve a publicar. La propagación de DNS puede tardar hasta 24–48 h.</p>
          </div>
        </div>

        <div className="border-t border-white/10 pt-5">
          <h3 className="font-semibold mb-1">Imagen para compartir (WhatsApp / redes)</h3>
          <p className="text-xs text-neutral-500 mb-3">Se muestra al compartir tu tienda. Si no subes una, usamos tu banner o logo automáticamente.</p>
          <div className="flex items-center gap-3">
            <div className="w-28 h-16 rounded-lg border border-white/15 overflow-hidden bg-black/40 flex items-center justify-center shrink-0">
              {(store.share_image_asset_id || store.banner_asset_id || store.logo_asset_id)
                ? <img src={assetUrl(`/api/assets/${store.share_image_asset_id || store.banner_asset_id || store.logo_asset_id}`)} alt="" className="w-full h-full object-cover" />
                : <UploadSimple size={18} className="text-neutral-600" />}
            </div>
            <Button variant="outline" onClick={() => shareRef.current?.click()} className="border-white/15" data-testid="store-share-image-upload"><UploadSimple size={16} className="mr-1" /> Subir imagen</Button>
            {store.share_image_asset_id && <button onClick={() => set({ share_image_asset_id: null })} className="text-xs text-rose-400 hover:text-rose-300" data-testid="store-share-image-clear">Quitar</button>}
            <input ref={shareRef} type="file" accept="image/*" hidden onChange={(e) => upload(e, "share_image_asset_id")} />
          </div>
          {store.slug && (
            <div className="mt-4">
              <Label>Enlace para compartir</Label>
              <p className="text-[11px] text-neutral-500 mb-2">Comparte este enlace en WhatsApp o redes para que se vea la vista previa con imagen. Lleva a tu tienda automáticamente.</p>
              <div className="flex items-center gap-2 rounded-lg bg-black/40 border border-white/10 px-3 py-2">
                <span className="text-sm text-neutral-300 truncate flex-1" data-testid="store-share-url">{shareUrl}</span>
                <button onClick={() => { navigator.clipboard.writeText(shareUrl); toast.success(t("storesettings.copy")); }} className="text-neutral-400 hover:text-gold" title={t("storesettings.copy")} data-testid="copy-share-url"><Copy size={16} /></button>
                <a href={shareUrl} target="_blank" rel="noreferrer" className="text-neutral-400 hover:text-gold" data-testid="open-share-url"><ArrowSquareOut size={16} /></a>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Store orders */}
      <div className="mt-8" data-testid="store-orders-section">
        <div className="flex items-center gap-2 mb-4"><Receipt size={20} className="text-gold" /><h2 className="text-lg font-semibold">{t("storesettings.ordersTitle")}</h2></div>
        {orders.length === 0 ? (
          <div className="border border-dashed border-white/15 rounded-xl p-10 text-center text-neutral-500 text-sm">{t("storesettings.ordersEmpty")}</div>
        ) : (
          <div className="space-y-2">
            {orders.map((o) => (
              <div key={o.id} className="border border-white/10 rounded-xl p-4 bg-[#121212]" data-testid={`store-order-${o.id}`}>
                <div className="flex items-center justify-between">
                  <div>
                    <span className="font-semibold">${Number(o.amount).toLocaleString()}</span>
                    <span className="text-neutral-500 text-sm ml-2">{o.buyer_email}</span>
                  </div>
                  <span className={`text-[10px] uppercase tracking-wider px-2 py-1 rounded-full ${o.status === "paid" ? "bg-emerald-500/20 text-emerald-300" : o.status === "failed" || o.status === "error" ? "bg-red-500/20 text-red-300" : "bg-white/10 text-neutral-400"}`}>{o.status === "paid" ? t("orders.status.paid") : o.status === "pending" ? t("success.pendingTitle") : o.status}</span>
                </div>
                <div className="text-xs text-neutral-400 mt-2">
                  {(o.items || []).map((it, i) => <div key={i}>{it.name} ×{it.quantity} {it.customization_text ? `— ${it.customization_text}` : ""}</div>)}
                </div>
                {o.application_fee > 0 && <div className="text-[11px] text-neutral-600 mt-1">{t("storesettings.commissionHephy", { amount: Number(o.application_fee).toLocaleString() })}</div>}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
